<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-stepper :steps="rmSteps" :current="currentStep" />
        <hm-spinner v-if="!ready" />
        <Loading :active="busy" :is-full-page="false">
          <slot>
            <hm-spinner />
          </slot>
        </Loading>
        <hm-alert />

        <b-form @submit.prevent="onSubmit" v-if="ready">
          <h4>Step 5: Create A Risk Management Plan</h4>
          <hr />

          <h6 class="pb-3">
            Threat:
            <strong>{{threat.threat}}</strong>
            <hm-popover v-if="threat.threatInfo" :title="threat.threat" :content="threat.threatInfo" />
          </h6>

          <h6 class="pb-3">
            Vulnerability:
            <strong>{{threat.vuln}}</strong>
            <hm-popover v-if="threat.vulnInfo" :title="threat.vuln" :content="threat.vulnInfo" />
          </h6>

          <h6 class="pb-3">
            Timeline:
            <strong>
              <span class="text-danger" v-if="threat.risk >= 20">Immediate implementation of corrective measures</span>
              <span class="text-warning" v-if="threat.risk >= 12 && threat.risk <= 19">Near future implementation of corrective measures</span>
              <span class="text-primary" v-if="threat.risk >= 6 && threat.risk <= 11">Earliest convenience implementation of corrective measures</span>
              <span class="text-success" v-if="threat.risk <= 5">Least urgent implementation of corrective measures</span>
            </strong>
          </h6>
          <hr />

          <div v-if="safeguardsPos.length > 0" class="pb-3">
            Safeguards currently implemented to protect against the threat:
            <div v-for="(item, index) in safeguardsPos" :key="index" class="pt-2">
              <div class="pl-3">
                <i class="icon-check text-success"></i>
                {{item.title}}
                <hm-popover v-if="item.desc || item.posList" :title="item.title" :content="item.desc" :content-list="item.posList" />
              </div>
            </div>
          </div>
          <hr />
          <div v-if="safeguardsNeg.length > 0" class="pb-3">
            <strong>Decide whether or not to implement the below safeguards meant to reduce the likelihood that the threat exploits the vulnerability or reduce the potential impact to your business should the vulnerability be exploited.</strong>
            <div role="tablist" class="pt-4">
              <b-card no-body class="mb-1" v-for="(item, index) in safeguardsNeg" :key="index">
                <b-card-header header-tag="header" class="p-2" role="tab" v-b-toggle="'accordian-' + item.id">
                  {{item.title}}
                  <div class="when-opened float-right px-2">
                    <i class="icon-arrow-down small pl-2"></i>
                  </div>
                  <b-badge
                    v-if="submitted && (errors.has('reason-' + item.id) || errors.has('reason-details-' + item.id) || errors.has('assignee-' + item.id) || errors.has('list-' + item.id) || errors.has('cost-' + item.id) || errors.has('duedate-' + item.id))"
                    class="float-right px-2 text-uppercase"
                    variant="danger"
                  >Needs Attention</b-badge>
                  <b-badge v-if="rm.safeguards[item.id].approved" class="float-right px-2 text-uppercase" variant="primary">Done</b-badge>
                </b-card-header>

                <b-collapse :id="'accordian-' + item.id" visible accordion="plan-accordion" role="tabpanel">
                  <b-card-body>
                    <p v-if="item.type == 'required'">
                      <b-badge class="text-uppercase" variant="warning">HIPAA Required Safeguard</b-badge>
                    </p>

                    <p class="prewrap">{{item.desc}}</p>

                    <b-form-group v-if="item.msg && item.msg.length > 0" label="Here's what we know:" label-class="font-weight-bold">
                      <p class="prewrap">{{item.msg}}</p>
                    </b-form-group>

                    <b-form-group v-if="item.type == 'addressable'" label="Do you plan to implement this safeguard?" label-class="font-weight-bold">
                      <hm-switch class="mx-1" v-model="rm.safeguards[item.id].implement" data-type="yesno" :disabled="busy" @change="rm.safeguards[item.id].approved = false" />
                    </b-form-group>

                    <b-form-group v-if="!rm.safeguards[item.id].implement" label="Why not? (select the primary reason)" label-class="font-weight-bold">
                      <div v-for="(value, key) in riskMap.SafeguardReason" :key="key">
                        <hm-switch
                          :name="'reason-' + item.id"
                          type="radio"
                          class="mx-1 p-1"
                          size="sm"
                          :label="value.title"
                          v-model="rm.safeguards[item.id].reason"
                          :value="key"
                          data-type="check"
                          v-validate="'required'"
                          :disabled="busy"
                          :label-class="{ 'is-invalid': errors.has('reason-' + item.id) && submitted }"
                          @change="rm.safeguards[item.id].reasonDetails = ''; rm.safeguards[item.id].approved = false"
                        >
                          <hm-popover v-if="value.popover" placement="top" :title="value.title">{{value.popover}}</hm-popover>
                        </hm-switch>
                        <b-form-textarea
                          v-if="value.details && rm.safeguards[item.id].reason == key"
                          no-resize
                          :rows="3"
                          :name="'reason-details-' + item.id"
                          class="form-control mt-1 mb-2"
                          :placeholder="value.details"
                          v-model="rm.safeguards[item.id].reasonDetails"
                          v-validate="'required'"
                          :disabled="busy"
                          :class="{ 'is-invalid': errors.has('reason-details-' + item.id) && submitted }"
                        />
                      </div>
                    </b-form-group>

                    <b-form-group v-if="rm.safeguards[item.id].implement" :label="item.listTitle" label-class="font-weight-bold">
                      <div v-if="item.type == 'addressable'">
                        <hm-switch
                          v-for="listItem in item.negList"
                          :key="listItem"
                          :name="'list-' + item.id"
                          class="mx-1 p-1"
                          size="sm"
                          :value="listItem"
                          :label="listItem"
                          v-model="rm.safeguards[item.id].list"
                          data-type="check"
                          :disabled="busy"
                          v-validate="'required'"
                          :label-class="{ 'is-invalid': errors.has('list-' + item.id) && submitted }"
                          @change="rm.safeguards[item.id].approved = false"
                        />
                      </div>
                      <div v-else>
                        <ul>
                          <li v-for="listItem in item.negList" :key="listItem">{{listItem}}</li>
                        </ul>
                      </div>
                    </b-form-group>

                    <b-form-group v-if="rm.safeguards[item.id].implement" label="What is the estimated cost (first year) to implement this safeguard?" label-class="font-weight-bold">
                      <b-input-group class="mb-3">
                        <b-input-group-prepend>
                          <b-input-group-text>$</b-input-group-text>
                        </b-input-group-prepend>
                        <vue-money
                          :name="'cost-' + item.id"
                          :precision="0"
                          v-model="rm.safeguards[item.id].cost"
                          :disabled="busy"
                          v-validate="'required'"
                          :class="{ 'p-2': true, 'is-invalid': errors.has('cost-' + item.id) }"
                          @change="rm.safeguards[item.id].approved = false"
                        />
                      </b-input-group>
                    </b-form-group>

                    <b-form-group v-if="rm.safeguards[item.id].implement" label="Who is responsible for making sure this safeguard gets implemented?" label-class="font-weight-bold">
                      <b-row>
                        <b-col cols="6">
                          <b-form-select
                            :name="'assignee-' + item.id"
                            class="form-control"
                            :options="workforceNames"
                            v-model="rm.safeguards[item.id].assignee"
                            :disabled="busy"
                            v-validate="'required'"
                            :class="{ 'is-invalid': errors.has('assignee-' + item.id) && submitted }"
                            @change="rm.safeguards[item.id].approved = false"
                          >
                            <template slot="first">
                              <option :value="''" disabled>-- Please select an option --</option>
                            </template>
                          </b-form-select>
                        </b-col>
                      </b-row>
                    </b-form-group>

                    <b-form-group v-if="rm.safeguards[item.id].implement" label="When do you want to complete the implementation of this safeguard (due date)?" label-class="font-weight-bold">
                      <b-row>
                        <b-col cols="6">
                          <vue-datepicker
                            :name="'duedate-' + item.id"
                            placeholder="YYYY-MM-DD"
                            format="yyyy-MM-dd"
                            v-model="rm.safeguards[item.id].dueDate"
                            bootstrap-styling
                            clear-button
                            clear-button-icon="fa fa-times-circle"
                            :disabledDates="disabledDates"
                            :disabled="busy"
                            v-validate="'required'"
                            :input-class="{ 'bg-white': true, 'is-invalid': errors.has('duedate-' + item.id) && submitted }"
                            @change="rm.safeguards[item.id].approved = false"
                          />
                        </b-col>
                      </b-row>
                    </b-form-group>
                  </b-card-body>
                </b-collapse>
              </b-card>
            </div>
          </div>
          <div v-else>
            <!-- no neg safeguards -->
            <strong>
              <div class="pl-3">
                <i class="icon-check text-success"></i>
                No additional safeguards required!
              </div>
            </strong>
          </div>

          <hr />
          <b-row>
            <b-col cols="6">
              <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/risk/plan">Back</b-button>
            </b-col>
            <b-col cols="6" class="text-right">
              <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Continue</b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { riskService } from '@/services'
import Enumerable from 'linq/linq.js';

export default {
  name: 'RiskPlan',
  props: ['id'],
  data() {
    return {
      rm: {
        seed: {
          workforce: {}
        },
        threats: [],
        safeguards: {}
      },
      threat: {},
      safeguards: [],
      disabledDates: {
        to: new Date(Date.now() - 8640000)
      },
      ready: false,
      busy: false,
      submitted: false,
      currentStep: 5
    }
  },
  computed: {
    ...mapState(['rmSteps']),
    ...mapState('maps', ['inventoryMap', 'riskMap', 'safeguardsMap', 'threatMap']),
    safeguardsPos() {
      return Enumerable.from(this.safeguards)
        .where(x => { return x.posEval && this.threat.safeguards.indexOf(x.id) > -1; })
        .toArray()
    },
    safeguardsNeg() {
      return Enumerable.from(this.safeguards)
        .where(x => { return x.negEval && (this.threat.safeguards.indexOf(x.id) > -1) && (x.type == 'required' || x.type == 'addressable'); })
        .toArray();
    },
    workforceNames() {
      return Enumerable.from(this.rm.seed.workforce).where(x => { return x.value.active; }).select(x => { return x.value.name; }).toArray();
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertError: 'alert/error',
      alertException: 'alert/exception'
    }),
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            // flag safeguards complete
            for (let i = 0; i < this.safeguardsNeg.length; i++) {
              this.rm.safeguards[this.safeguardsNeg[i].id].approved = true;
            }

            // flag threat plan complete
            for (let i = 0; i < this.rm.threats.length; i++) {
              if (this.threat.id == this.rm.threats[i].id) this.rm.threats[i].plan = true;
            }

            return riskService.setRiskManagement(this.rm)
              .then(data => {
                this.$router.push('/risk/plan');
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving risk management.' });
              });
          } else {
            this.alertError('Please complete each item and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onLoad() {
      this.ready = false;

      Promise.all([
        this.$store.dispatch('maps/fetchInventoryMap'),
        this.$store.dispatch('maps/fetchRiskMap'),
        this.$store.dispatch('maps/fetchSafeguardsMap'),
        this.$store.dispatch('maps/fetchThreatMap'),
        riskService.getCurrentRiskManagement()
      ]).then((values) => {
        let rm = values[4];

        // set threat
        this.threat = Enumerable.from(this.threatMap)
          .join(rm.threats,
            (o) => { return o.id + ':' + o.revision; },
            (i) => { return i.id + ':' + i.revision; },
            (o, i) => { return { id: o.id, threat: o.threat, threatInfo: o.threatInfo, vuln: o.vuln, vulnInfo: o.vulnInfo, active: i.active, plan: i.plan, safeguards: o.safeguards, risk: i.chance * i.impact }; })
          .where(x => { return x.id == this.id; })
          .firstOrDefault();

        this.safeguards = riskService.updateSafeguards(rm, this.threatMap, this.safeguardsMap, this.inventoryMap);
        this.rm = rm;
      }).catch((err) => {
        this.alertException({ err, message: 'Error loading risk management data.' });
      }).finally(() => {
        this.ready = true;
      })
    }
  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>

<style>
.collapsed > .when-opened,
:not(.collapsed) > .when-closed {
  display: none;
}
.prewrap {
  white-space: pre-wrap;
}
</style>